import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import App from "./../App";
import { useMapsInstance } from "./../CustomHooks";
import placeholders from "./../Global/placeholderAutocomplete";
import Queries from "./../Queries";
import formatSchedule from "./../Global/formatSchedule";
import getIfOpen from "../Global/getIfOpen";
import getPostalAbbreviation from "../Global/getPostalAbbreviation";

export const GlobalContext = React.createContext();

const AppWrapper = () => {
  const langcode = drupalSettings.bacLangcode;
  const radius = 2000;
  const aliasCountry = location.pathname.match(/(?<=\/)[a-z]{2}-[a-z]{2}(?=\/)/);
  const [filterCardState, setFilterCardState] = useState("close");
  const [resultsState, setResultsState] = useState(false);
  const [currentPopUp, setCurrentPopUp] = useState(null);
  const [categoryFilters, setCategoryFilters] = useState(null);
  const [categorySelected, setCategorySelected] = useState(null);
  const [branchSchedulesVisibility, setBranchSchedulesVisibility] = useState(
    null
  );
  const [backButtonVisibility, setBackButtonVisibility] = useState(false);
  const [backButtonType, setBackButtonType] = useState("search");
  const [activeFilters, setActiveFilters] = useState([]);
  const [currentCountry, setCurrentCountry] = useState(null);
  const [countriesInfo, setCountriesInfo] = useState(null);
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState("");
  const [initialPos, setInitialPos] = useState({});
  const [mapsInstance, setMapsInstance] = useMapsInstance({});
  const [markersVisibility, setMarkersVisibility] = useState(false);
  const [geolocationAvailable, setGeolocationAvailable] = useState(true);
  const [filtersVisibility, setFiltersVisibility] = useState(true);
  const [advancedFiltersVisibility, setAdvancedFiltersVisibility] = useState(
    false
  );
  const [resultsVisibility, setResultsVisibility] = useState(false);
  const [positionState, setPositionState] = useState("loading");
  const [placeholderText, setPlaceholderText] = useState("");
  const [isShareButtonClicked, setisShareButtonClicked] = useState(false);
  const [hasQueryParam, setHasQueryParam] = useState(false);
  const [queryParams, setQueryParams] = useState(false);
  const [helpCenterLinks, setHelpCenterLinks] = useState(null);

  const [getBranchByID, { data: dataByID }] = useLazyQuery(
    Queries("branchByID"),
    {
      variables: {
        nid: parseInt(queryParams.id, 10) || 0,
      },
    }
  );

  const [getBranchByTitle, { data: dataByTitle }] = useLazyQuery(
    Queries("branchByTitle"),
    {
      variables: {
        title: queryParams.name,
      },
    }
  );

  const [ctaLink, setCtaLink] = useState({});

  // Sets the initial location using a placeholder for the current langcode
  useEffect(() => {
    let { location } = placeholders(langcode);

    setInitialPos({
      lat: location.lat,
      lng: location.lng,
    });
  }, [langcode]);

  // If available, Sets a new location from navigator.geolocation HTML5 API.
  useEffect(() => {
    if (navigator && navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then(() => {
        navigator.geolocation.getAccurateCurrentPosition(
          (position) => {
            if (position) {
              setPositionState("success");
              setInitialPos({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
              setGeolocationAvailable(true);
            } else {
              setPositionState("failed");
              setGeolocationAvailable(false);
            }
          },
          () => {
            setPositionState("failed");
            setGeolocationAvailable(false);
          },
          () => {},
          { desiredAccuracy: 50, maxWait: 10000 }
        );
      });
    } else {
      navigator.geolocation.getAccurateCurrentPosition(
        (position) => {
          if (position) {
            setPositionState("success");
            setInitialPos({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            setGeolocationAvailable(true);
          } else {
            setPositionState("failed");
            setGeolocationAvailable(false);
          }
        },
        () => {
          setPositionState("failed");
          setGeolocationAvailable(false);
        },
        () => {},
        { desiredAccuracy: 50, maxWait: 10000 }
      );
    }
  }, [setInitialPos, setGeolocationAvailable, positionState]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const nameParam = searchParams.get("nombre-sucursal");
    const idParam = searchParams.get("id-sucursal");

    if (nameParam || idParam) {
      setHasQueryParam(true);
      setQueryParams({
        name: nameParam,
        id: idParam,
      });
    }
  }, []);

  useEffect(() => {
    if (queryParams.id) {
      getBranchByID();
    } else if (queryParams.name) {
      getBranchByTitle();
    }
  }, [queryParams, getBranchByID, getBranchByTitle]);

  useEffect(() => {
    if (countriesInfo && !currentCountry) {
      const country = countriesInfo.availableLanguages.find((country) =>
        aliasCountry.includes(country.id)
      );
      setCurrentCountry(country);
    }
  }, [aliasCountry, countriesInfo, currentCountry]);

  useEffect(() => {
    if (dataByID) {
      const queriedBranch = dataByID.officeBranchById;
      const formatedDate = formatSchedule(queriedBranch.fieldSchedule);
      const formatedCounterDate = formatSchedule(
        queriedBranch.fieldScheduleCounter
      );
      let scheduleState = getIfOpen(
        formatedDate.currentDate,
        null,
        formatedDate.otherDaysOpen
      );
      let scheduleCounterState = getIfOpen(
        formatedCounterDate.currentDate,
        null,
        formatedCounterDate.otherDaysOpen
      );

      if (scheduleState === "Abierto" || scheduleCounterState === "Abierto") {
        queriedBranch.branchScheduleState = "Abierto";
      } else if (
        scheduleState === "NotFound" &&
        scheduleCounterState === "NotFound"
      ) {
        queriedBranch.branchScheduleState = "NotFound";
      } else {
        queriedBranch.branchScheduleState = "Cerrado";
      }

      if (queriedBranch.fieldOfficeBranchImage)
        queriedBranch.image = queriedBranch.fieldOfficeBranchImage;
      // fieldOfficeBranchType is already a plain string from GraphQL 4.x
      queriedBranch.geographyLocation =
        queriedBranch[
          `fieldGeographyLocation${getPostalAbbreviation(langcode)}`
        ];

      setBranches([queriedBranch]);
      setBackButtonVisibility(true)
      setFiltersVisibility(false);
      setResultsState(true);
      setMarkersVisibility(true);
      setCurrentPopUp(queriedBranch);
      setInitialPos({
        lat: queriedBranch.fieldCoordinates.lat,
        lng: queriedBranch.fieldCoordinates.lng,
      });
    }
  }, [dataByID, langcode]);

  useEffect(() => {
    if (dataByTitle) {
      const queriedBranch = dataByTitle.officeBranchByTitle;
      const formatedDate = formatSchedule(queriedBranch.fieldSchedule);
      const formatedCounterDate = formatSchedule(
        queriedBranch.fieldScheduleCounter
      );
      let scheduleState = getIfOpen(
        formatedDate.currentDate,
        null,
        formatedDate.otherDaysOpen
      );
      let scheduleCounterState = getIfOpen(
        formatedCounterDate.currentDate,
        null,
        formatedCounterDate.otherDaysOpen
      );

      if (scheduleState === "Abierto" || scheduleCounterState === "Abierto") {
        queriedBranch.branchScheduleState = "Abierto";
      } else if (
        scheduleState === "NotFound" &&
        scheduleCounterState === "NotFound"
      ) {
        queriedBranch.branchScheduleState = "NotFound";
      } else {
        queriedBranch.branchScheduleState = "Cerrado";
      }

      if (queriedBranch.fieldOfficeBranchImage)
        queriedBranch.image = queriedBranch.fieldOfficeBranchImage;
      // fieldOfficeBranchType is already a plain string from GraphQL 4.x
      queriedBranch.geographyLocation =
        queriedBranch[
          `fieldGeographyLocation${getPostalAbbreviation(langcode)}`
        ];

      setBranches([queriedBranch]);
      setBackButtonVisibility(true);
      setFiltersVisibility(false);
      setResultsState(true);
      setMarkersVisibility(true);
      setCurrentPopUp(queriedBranch);
      setInitialPos({
        lat: queriedBranch.fieldCoordinates.lat,
        lng: queriedBranch.fieldCoordinates.lng,
      });
    }
  }, [dataByTitle, langcode]);

  const addActiveFilter = (filter, isChecked = false) => {
    if (filter === "none") {
      setActiveFilters([]);
    } else {
      if (activeFilters.indexOf(filter) === -1) {
        setActiveFilters((filtersArray) => [...filtersArray, filter]);
      } else {
        if (!isChecked) {
          setActiveFilters((filtersArray) => {
            let newFilterArray = filtersArray.filter(function (value) {
              if (value !== filter) {
                return value;
              }
            });
            return newFilterArray;
          });
        }
      }
    }
  };

  return (
    <GlobalContext.Provider
      value={{
        langcode,
        radius,
        currentPopUp,
        setCurrentPopUp,
        resultsState,
        setResultsState,
        branches,
        setBranches,
        currentBranch,
        setCurrentBranch,
        initialPos,
        setInitialPos,
        activeFilters,
        setActiveFilters,
        mapsInstance,
        setMapsInstance,
        markersVisibility,
        setMarkersVisibility,
        geolocationAvailable,
        setGeolocationAvailable,
        filtersVisibility,
        setFiltersVisibility,
        advancedFiltersVisibility,
        setAdvancedFiltersVisibility,
        resultsVisibility,
        setResultsVisibility,
        positionState,
        setPositionState,
        placeholderText,
        setPlaceholderText,
        isShareButtonClicked,
        setisShareButtonClicked,
        setCountriesInfo,
        countriesInfo,
        currentCountry,
        hasQueryParam,
        queryParams,
        setQueryParams,
        ctaLink,
        setCtaLink,
        helpCenterLinks,
        setHelpCenterLinks,
        backButtonVisibility,
        setBackButtonVisibility,
        backButtonType,
        setBackButtonType,
        addActiveFilter,
        filterCardState,
        setFilterCardState,
        branchSchedulesVisibility,
        setBranchSchedulesVisibility,
        categoryFilters,
        setCategoryFilters,
        categorySelected,
        setCategorySelected,
      }}
    >
      <App />
    </GlobalContext.Provider>
  );
};

export default AppWrapper;
